/**
 * CyberSells Visual Effects
 * Matrix rain, ASCII backgrounds, and cyberpunk animations
 * Optimized for performance with passive listeners and IntersectionObserver
 */

(function() {
  'use strict';

  // Enable effects based on performance and user preferences
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = window.innerWidth < 768;
  
  // Singleton instance to prevent double initialization
  let _instance = null;

  class CyberEffects {
    constructor() {
      // Return existing instance if already created (singleton pattern)
      if (_instance) {
        if (window.cyberSellsConfig?.debug) {
          console.log('[CyberEffects] Already initialized, returning existing instance...');
        }
        return _instance;
      }
      
      // Store this instance
      _instance = this;
      
      this.init();
    }

    init() {
      if (prefersReducedMotion) return;

      this.enableScanlines();
      
      if (!isMobile) {
        this.createMatrixRain();
        this.createASCIIBackground();
        this.createGrid();
      }

      this.initGlitchEffects();
    }

    enableScanlines() {
      // Batch DOM write
      requestAnimationFrame(() => {
        document.body.classList.add('cs-scanlines');
      });
    }

    createMatrixRain() {
      const container = document.createElement('div');
      container.className = 'cs-matrix-rain';
      
      // Character set for matrix rain effect
      const characters = '01ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      
      // Batch DOM reads
      const windowWidth = window.innerWidth;
      const columnCount = Math.floor(windowWidth / 20);

      // Build all columns before adding to DOM (prevent layout thrash)
      const fragment = document.createDocumentFragment();
      
      for (let i = 0; i < columnCount; i++) {
        const column = document.createElement('div');
        column.className = 'cs-matrix-column';
        column.style.left = (i * 20) + 'px';
        column.style.animationDuration = (Math.random() * 3 + 2) + 's';
        column.style.animationDelay = (Math.random() * 2) + 's';

        let text = '';
        const height = Math.floor(Math.random() * 20) + 10;
        for (let j = 0; j < height; j++) {
          text += characters.charAt(Math.floor(Math.random() * characters.length)) + '\n';
        }
        column.textContent = text;

        fragment.appendChild(column);
      }
      
      container.appendChild(fragment);

      // Single DOM write
      requestAnimationFrame(() => {
        document.body.appendChild(container);
      });
    }

    createASCIIBackground() {
      const container = document.createElement('div');
      container.className = 'cs-ascii-bg';
      
      // Character set for ASCII background
      const chars = '01ABCD';
      let ascii = '';
      
      // Batch DOM reads
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      const rows = Math.floor(windowHeight / 15);
      const cols = Math.floor(windowWidth / 8);

      for (let i = 0; i < rows * 2; i++) {
        for (let j = 0; j < cols; j++) {
          ascii += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        ascii += '\n';
      }

      container.textContent = ascii;
      
      // Single DOM write
      requestAnimationFrame(() => {
        document.body.appendChild(container);
      });
    }

    createGrid() {
      const grid = document.createElement('div');
      grid.className = 'cs-grid-bg';
      
      // Single DOM write
      requestAnimationFrame(() => {
        document.body.appendChild(grid);
      });
    }

    initGlitchEffects() {
      // Use IntersectionObserver for efficient scroll-based effects
      const glitchElements = document.querySelectorAll('[data-glitch]');
      
      if (glitchElements.length === 0) return;
      
      // Check if IntersectionObserver is supported (fallback for older browsers)
      if (typeof IntersectionObserver === 'undefined') {
        if (window.cyberSellsConfig?.debug) {
          console.log('[CyberEffects] IntersectionObserver not supported, skipping glitch effects');
        }
        return;
      }
      
      // Setup intersection observer for performance
      const observerOptions = {
        root: null,
        rootMargin: '50px',
        threshold: 0.1
      };
      
      const glitchObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const el = entry.target;
            
            // Prevent creating multiple intervals for the same element (idempotent)
            if (el._glitchInterval) {
              return;
            }
            
            // Batch DOM writes
            requestAnimationFrame(() => {
              el.classList.add('cs-glitch');
              el.setAttribute('data-text', el.textContent);
            });
            
            // Random glitch trigger
            const glitchInterval = setInterval(() => {
              if (Math.random() > 0.95) {
                requestAnimationFrame(() => {
                  el.style.animation = 'none';
                  setTimeout(() => {
                    requestAnimationFrame(() => {
                      el.style.animation = '';
                    });
                  }, 100);
                });
              }
            }, 2000);
            
            // Store interval reference for cleanup
            el._glitchInterval = glitchInterval;
          } else {
            // Clean up when element leaves viewport
            if (entry.target._glitchInterval) {
              clearInterval(entry.target._glitchInterval);
              entry.target._glitchInterval = null;
            }
          }
        });
      }, observerOptions);
      
      // Observe all glitch elements
      glitchElements.forEach(el => {
        glitchObserver.observe(el);
      });
    }
  }

  // Initialize effects when DOM is ready (idempotent with once: true)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new CyberEffects(), { once: true });
  } else {
    new CyberEffects();
  }

})();
